import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  Loading,
  PageableDataSourceV2,
  PageableListV2Dto,
  SnackbarService,
  TemplateDirective,
  activateLoading,
  catchApiError,
  getId,
} from '@fleet-beat/core';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  delay,
  filter,
  isObservable,
  map,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { SimpleField } from '../../abstracts/simple-field';
import { AnexConfig } from '../../models/anex-config.model';
import { DataAutocompleteStorageParams } from '../../models/data-autocomplete-storage-params.model';

@Component({
  selector: 'fb-data-autocomplete',
  templateUrl: './data-autocomplete.component.html',
  styleUrls: ['./data-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataAutocompleteComponent<ValueData>
  extends SimpleField<string | Partial<ValueData> | null>
  implements OnInit
{
  @Input() valueToString: (value: Partial<ValueData>) => string;
  @Input() createNew: (search: string) => Partial<ValueData>;
  @Input() optionTemplate: TemplateDirective<object>;
  @Input() allowEmpty: boolean = true;
  @Input() allowCreate: boolean = false;
  @Input() alwaysNewSearch: boolean = false;
  @Input() emitEventWhenLoadedById: boolean = false;
  @Input() loading: Loading = new Loading();
  @Input() notFoundMessage: string;
  @Input() multiple: boolean = false;
  @Input() clearValueAfterSelect: boolean = false;
  @Input() addButtonCaption: string;
  @Input() transformSelected: (selected: ValueData) => ValueData | Observable<ValueData>;
  @Input() prefix: AnexConfig;
  @Input() suffix: AnexConfig;
  @Input() templates: QueryList<TemplateDirective<object>>;
  @Input() noLabel: boolean;

  @Input()
  set disabledItems(items: ValueData[]) {
    if (items) {
      this.disabledItemsMap = items.filter(Boolean).reduce((result: Record<string, boolean>, item: ValueData) => {
        const key: string = getId(item);
        result[key] = true;
        return result;
      }, {});
    } else {
      this.disabledItemsMap = null;
    }
  }

  @Input()
  set storage(
    storage: PageableDataSourceV2<Observable<PageableListV2Dto<object>>, DataAutocompleteStorageParams<object>>,
  ) {
    this.privateStorage = storage;
  }
  get storage(): PageableDataSourceV2<Observable<PageableListV2Dto<object>>, DataAutocompleteStorageParams<object>> {
    return this.privateStorage;
  }

  @Input()
  set storageParams(params: Record<string, object | string | number | boolean>) {
    this.privateStorageParams = params;
    this.defaultStorageParams = params;
    this.setSearch('', false);
  }
  get storageParams(): Record<string, object | string | number | boolean> {
    return this.privateStorageParams;
  }

  endOfData: boolean = false;
  notFound: boolean = false;
  search: string;
  disabledItemsMap: Record<string, boolean> = null;
  lastPage: PageableListV2Dto<ValueData> = undefined;
  readonly filtering: Loading = new Loading();
  readonly autocompleteControl: FormControl = new FormControl();
  readonly filteredValuesSubject: BehaviorSubject<ValueData[]> = new BehaviorSubject([]);

  private privateStorage: PageableDataSourceV2<
    Observable<PageableListV2Dto<object>>,
    DataAutocompleteStorageParams<object>
  >;
  private privateStorageParams: Record<string, object | string | number | boolean>;
  private defaultStorageParams: Record<string, object | string | number | boolean>;
  private readonly size: number = 40;
  private preloadedValue: Partial<ValueData>;
  private subscription: Subscription = new Subscription();
  private resetSubscription: Subscription = new Subscription();

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional() @Self() public override ngControl: NgControl,
    private snackbarService: SnackbarService,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.autocompleteControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        filter((value: string | ValueData | null) => typeof value === 'string'),
        map((value: string | ValueData | null) => value.toString()),
      )
      .subscribe((value: string) => {
        this.lastPage = undefined;
        this.privateStorageParams = this.defaultStorageParams;
        this.filteredValuesSubject.next([]);
        this.setSearch(value, false);
      });
    this.setSearch('', false);
  }

  onClear(): void {
    this.privateStorageParams = this.defaultStorageParams;
    this.filteredValuesSubject.next([]);
    this.setValue(null);

    this.autocompleteControl.markAsTouched();
  }

  onInputBlur(): void {
    this.resetSearch();
  }

  /* eslint-disable-next-line @typescript-eslint/typedef */
  displayValue = (value: string | Partial<ValueData> | null): string => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return this.valueToString(value);
  };

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value['addNewValue']) {
      this.add();
      return;
    }

    if (!this.compareValues(this.value, event.option.value)) {
      this.filteredValuesSubject.next([]);

      this.transformValue(event.option.value)
        .pipe(untilComponentDestroyed(this), activateLoading(this.loading))
        .subscribe((value: ValueData) => {
          this.setValue(value);
        });
    }

    if (this.clearValueAfterSelect) {
      this.setValue(null);
    }
  }

  add(): void {
    if (this.allowCreate && this.search) {
      this.setValue(this.createNew(this.search.trim()) as ValueData);
    }
  }

  loadNext(): void {
    if (this.endOfData) return;

    const limit = this.lastPage?.limit ?? 25;
    const offset = (this.lastPage?.offset ?? 0) + limit;

    this.privateStorageParams = {
      ...this.storageParams,
      limit,
      offset,
    };

    this.filterValues(this.search, false);
  }

  protected override onWriteValue(value: string | ValueData | null): void {
    if (this.compareValues(this.autocompleteControl.value, value)) {
      return;
    }

    if (!value) {
      this.setSearch(null);
      return;
    }

    if (typeof value === 'string') {
      this.loadValueByText(value);
      return;
    }

    this.setValue(value);
  }

  private transformValue(value: ValueData): Observable<ValueData> {
    if (!this.transformSelected) {
      return of(value);
    }

    const result: ValueData | Observable<ValueData> = this.transformSelected(value);
    return isObservable(result) ? result : of(result);
  }

  private getValues(search: string): Observable<PageableListV2Dto<ValueData>> {
    return this.storage.getData({ params: { input: search }, extra: this.storageParams });
  }

  private resetSearch(): void {
    this.resetSubscription = new Subscription();
    this.resetSubscription.add(
      of(null)
        .pipe(untilComponentDestroyed(this), delay(200))
        .subscribe(() => {
          if (!this.autocompleteControl.value && this.value && this.allowEmpty) {
            this.onClear();
            return;
          }

          const value: string | Partial<ValueData> | null =
            typeof this.value !== 'string' ? this.value : this.preloadedValue;
          if (
            typeof this.autocompleteControl.value === 'string' &&
            !this.compareValues(this.displayValue(value), this.autocompleteControl.value)
          ) {
            this.setValue(value as ValueData, false);
          }
        }),
    );
  }

  private setSearch(search: string | Partial<ValueData> | null, isSetValue: boolean = true): void {
    this.endOfData = false;
    this.search = this.displayValue(search);

    if (isSetValue) {
      this.autocompleteControl.setValue(search, { emitEvent: false });
    }

    this.filterValues(this.search, true);
  }

  private setValue(value: ValueData | null, emitChangeEvent: boolean = true): void {
    this.resetSubscription.unsubscribe();
    this.endOfData = false;
    this.search = null;

    if (emitChangeEvent) {
      /* NOTE:
       * this line cause calling writeValue -> onWriteValue -> setSearch -> filterValues
       * filterValues method calls `this.from = this.from + this.size;`
       * after this line we again call filterValues method and this.from > 0,
       * so this.from must be set to 0 to avoid duplicates
       */
      this.value = value;
    }

    this.preloadedValue = value;
    this.autocompleteControl.setValue(value, { emitEvent: false });

    this.filterValues(this.alwaysNewSearch ? '' : this.displayValue(value), true);
  }

  private filterValues(search: string, cancelPrevious: boolean): void {
    if (cancelPrevious) {
      this.subscription.unsubscribe();
      this.subscription = new Subscription();
    }

    this.subscription.add(
      timer(300)
        .pipe(
          switchMap(() => {
            return this.getValues(search?.toLowerCase()).pipe(
              untilComponentDestroyed(this),
              activateLoading(this.filtering),
              catchApiError(this.snackbarService),
            );
          }),
        )
        .subscribe((data: PageableListV2Dto<ValueData>) => {
          if (!data?.data?.length) {
            this.notFound = true;
            this.filteredValuesSubject.next([]);
          } else {
            this.notFound = false;
            if (search !== this.search) {
              this.filteredValuesSubject.next(data.data);
            } else {
              this.filteredValuesSubject.next([...this.filteredValuesSubject.getValue(), ...data.data]);
            }
          }

          this.lastPage = data;
          this.endOfData = data.limit + data.offset >= data.total;
        }),
    );
  }

  private loadValueByText(id: string): void {
    this.storage
      .getById(id)
      .pipe(untilComponentDestroyed(this), activateLoading(this.loading), catchApiError(this.snackbarService))
      .subscribe((value: ValueData) => {
        this.setValue(value, value ? this.emitEventWhenLoadedById : true);
      });
  }

  private compareValues(value1: string | Partial<ValueData>, value2: string | Partial<ValueData>): boolean {
    if (value1 === value2) {
      return true;
    }
    const id1: string = getId(value1) || this.displayValue(value1);
    const id2: string = getId(value2) || this.displayValue(value2);
    return id1 === id2;
  }
}
